"use client";

import Link from "next/link";
import { useState, MouseEvent, ReactNode } from "react";
import { cn } from "@/utils/cn";

type Variant = "primary" | "secondary" | "danger";

type BaseProps = {
  label: string;
  className?: string;
  variant?: Variant;
};

type ButtonProps = BaseProps & {
  type?: "button" | "submit" | "reset";
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  link?: never;
};

type LinkProps = BaseProps & {
  link: string;
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
  type?: never;
};

type ActionButtonProps = ButtonProps | LinkProps;

const VARIANT_CLASS: Record<Variant, string> = {
  primary: "bg-primary hover:bg-primaryDark hover:shadow-button-hover",
  secondary:
    "bg-primary text-black hover:bg-primaryDark shadow-md hover:shadow-lg",
  danger: "bg-error hover:bg-primaryHover shadow-md hover:shadow-lg",
};

const BaseClass =
  "relative inline-flex items-center gap-[10px] overflow-hidden px-[38px] py-[10px] " +
  "font-semibold text-[15px] tracking-[0.01em] transition-all duration-200 ease-default";

export default function ActionButton(props: ActionButtonProps) {
  const { label, className, variant = "primary" } = props;
  const [hovered, setHovered] = useState(false);

  const sharedClass = cn(
    BaseClass,
    hovered ? "-translate-y-[2px]" : "translate-y-0",
    VARIANT_CLASS[variant],
    className
  );

  /* ---------- LINK ---------- */
  if ("link" in props) {
    return (
      <Link
        href={props.link ?? "/"}
        onClick={
          props.onClick as
            | ((e: MouseEvent<HTMLAnchorElement>) => void)
            | undefined
        }
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className={sharedClass}
      >
        <span className="relative z-[2] whitespace-nowrap">{label}</span>
        <Shine hovered={hovered} />
      </Link>
    );
  }

  /* ---------- BUTTON ---------- */
  return (
    <button
      type={props.type ?? "button"}
      onClick={props.onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={sharedClass}
    >
      <span className="relative z-[2] whitespace-nowrap">{label}</span>
      <Shine hovered={hovered} />
    </button>
  );
}

/* ---------- Shine overlay ---------- */
function Shine({ hovered }: { hovered: boolean }) {
  return (
    <span
      className={cn(
        "pointer-events-none absolute inset-0 z-[1]",
        "bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.25),transparent)]",
        "transition-transform duration-500 ease-out",
        hovered ? "translate-x-full" : "-translate-x-full"
      )}
    />
  );
}
